import { useEffect, useMemo } from 'react';

import { iaRa } from 'lib/helpers/utility';
import { useTableContext } from 'lib/context/TableContext/TableContext';

export const PRODUCT_NDC_FIELD = 'product_ndc';

export const MEDICATION_PRODUCT_NDC_GROUPING_PROPS = {
  groupDefaultExpanded: 0,
  groupDisplayType: 'custom',
  groupRemoveSingleChildren: true
};

export const productNdcGroupKeyCreator = (params) => {
  const value = params?.value;

  if (value == null || value === '') {
    const rowId = params?.data?.id ?? params?.node?.id ?? '';
    return `__no_product_ndc__${rowId}`;
  }

  return String(value).trim();
};

const hasDuplicateMedicationProductNdc = (medications = []) => {
  const seen = new Set();

  for (const medication of iaRa(medications)) {
    const productNdc =
      medication?.product_ndc == null || medication?.product_ndc === ''
        ? null
        : String(medication.product_ndc).trim();

    if (!productNdc) {
      continue;
    }

    if (seen.has(productNdc)) {
      return true;
    }

    seen.add(productNdc);
  }

  return false;
};

// Manages product_ndc row-grouping purely through ag-grid's column state.
// The keyCreator must live on the column def (see PRODUCT_NDC_FIELD col defs)
// so that re-rendering the cols (e.g. when permission state flips) cannot wipe
// it out. Touching `setColDefs` from here previously fought with parent
// useEffects that rebuild colDefs and caused rows to flicker / disappear.
export const useMedicationProductNdcGrouping = ({ medications = [], enabled = true } = {}) => {
  const { columnsReady, colDefs, gridApi } = useTableContext();
  const hasDuplicateProductNdc = useMemo(
    () => enabled && hasDuplicateMedicationProductNdc(medications),
    [enabled, medications]
  );

  useEffect(() => {
    if (!gridApi?.applyColumnState || !columnsReady) {
      return;
    }

    const hasProductNdcColumn = iaRa(colDefs).some((col) => col.field === PRODUCT_NDC_FIELD);
    if (!hasProductNdcColumn) {
      return;
    }

    const productNdcColumnState = iaRa(gridApi.getColumnState?.()).find(
      (col) => col.colId === PRODUCT_NDC_FIELD
    );
    const isGridGroupedByProductNdc =
      productNdcColumnState?.rowGroup === true && productNdcColumnState?.rowGroupIndex === 0;

    if (hasDuplicateProductNdc && !isGridGroupedByProductNdc) {
      gridApi.applyColumnState({
        state: [{ colId: PRODUCT_NDC_FIELD, rowGroup: true, rowGroupIndex: 0 }]
      });
    } else if (!hasDuplicateProductNdc && isGridGroupedByProductNdc) {
      gridApi.applyColumnState({
        state: [{ colId: PRODUCT_NDC_FIELD, rowGroup: false, rowGroupIndex: null }]
      });
    }
  }, [columnsReady, gridApi, hasDuplicateProductNdc, colDefs]);
};
